/**
 * 問題本文のリンク（docs/spec/problem-format.md §5.3、§6.2）。ブラウザでも使う（描画側の再確認）。
 * 許可先はレッスンと同じ lessonLinkTarget で検査し、そのうえで atcoder.jp を拒否する
 * （AtCoder へのリンクは front matter の source.url だけ。§2.6）。
 * レッスンの節へのディープリンク `#/lesson/<id>#<slug>` は router の parseHash が section 付きで受ける。
 */
import { lessonLinkTarget, type LessonLinkTarget } from '../lessons/links.js';
import { parseHash } from '../router/route.js';

export const ATCODER_HOST = 'atcoder.jp';

export interface ProblemLinkTarget extends LessonLinkTarget {
  /** アプリ内のレッスンへのリンクなら、その id と節（section は decode・NFC 済み）。 */
  lesson?: { id: string; section?: string };
  /** ラボ・メーターへのリンク。 */
  tool?: 'lab' | 'meter';
}

/** atcoder.jp へのリンクか（許可先の検査を通った後の判定）。 */
function isAtcoder(target: LessonLinkTarget): boolean {
  if (!target.external) return false;
  try {
    return new URL(target.href).hostname === ATCODER_HOST;
  } catch {
    return false;
  }
}

/**
 * 問題本文のリンク先を確かめる。許可しないものは null（描画側は文字だけを描く）。
 * parse（ビルド時）と描画（ブラウザ）の両方がこの関数を使う。
 */
export function problemLinkTarget(url: string): ProblemLinkTarget | null {
  const target = lessonLinkTarget(url);
  if (target === null || isAtcoder(target)) return null;
  if (target.external) return target;
  const route = parseHash(target.href);
  if (route.name === 'lesson') {
    const lesson: { id: string; section?: string } = { id: route.lessonId };
    if (route.section !== undefined) lesson.section = route.section;
    return { ...target, lesson };
  }
  if (route.name === 'lab' || route.name === 'meter') return { ...target, tool: route.name };
  return target;
}

/** problemLinkTarget が拒否した理由（parse の文言用）。許可なら null。 */
export function problemLinkRejection(url: string): string | null {
  const target = lessonLinkTarget(url);
  if (target === null) return `許可していないリンク先です: ${url}`;
  if (isAtcoder(target)) {
    return `本文に atcoder.jp へのリンクを書きません（source.url を画面が出します）: ${url}`;
  }
  return null;
}
